import { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { 
  Plus, 
  Trash2, 
  RefreshCw, 
  Volume2, 
  Mic, 
  Settings2,
  AlertCircle,
  CheckCircle2,
  Info,
  Download,
  CloudDownload
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from "@/components/ui/alert";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function VoiceModelSettings() {
  // TTS Voices State
  const [ttsVoices, setTtsVoices] = useState([]);
  const [loadingTts, setLoadingTts] = useState(false);
  
  // Realtime Voices State
  const [realtimeVoices, setRealtimeVoices] = useState([]);
  const [loadingRealtime, setLoadingRealtime] = useState(false);
  
  // Whisper Models State
  const [whisperModels, setWhisperModels] = useState([]);
  const [loadingWhisper, setLoadingWhisper] = useState(false);
  
  // Sync States
  const [syncingVoices, setSyncingVoices] = useState(false);
  const [syncingWhisper, setSyncingWhisper] = useState(false);
  
  // Dialog States
  const [addVoiceDialogOpen, setAddVoiceDialogOpen] = useState(false);
  const [addModelDialogOpen, setAddModelDialogOpen] = useState(false);
  const [currentVoiceType, setCurrentVoiceType] = useState("realtime");
  
  // New Voice Form
  const [newVoice, setNewVoice] = useState({
    id: "",
    name: "",
    description: "",
    gender: "neutral",
    style: "",
    accent: "American",
    tier: "standard"
  });
  
  // New Model Form
  const [newModel, setNewModel] = useState({
    id: "",
    name: "",
    description: ""
  });

  useEffect(() => {
    fetchTtsVoices();
    fetchRealtimeVoices();
    fetchWhisperModels();
  }, []);

  const fetchTtsVoices = async () => {
    setLoadingTts(true);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/admin/openai-voices/tts`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTtsVoices(response.data.voices || []);
    } catch (error) {
      toast.error("Failed to fetch TTS voices");
      console.error(error);
    } finally {
      setLoadingTts(false);
    }
  };

  const fetchRealtimeVoices = async () => {
    setLoadingRealtime(true);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/admin/openai-voices/realtime`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRealtimeVoices(response.data.voices || []);
    } catch (error) {
      toast.error("Failed to fetch Realtime voices");
      console.error(error);
    } finally {
      setLoadingRealtime(false);
    }
  };

  const fetchWhisperModels = async () => {
    setLoadingWhisper(true);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/admin/whisper-models`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setWhisperModels(response.data.models || []);
    } catch (error) {
      toast.error("Failed to fetch Whisper models");
      console.error(error);
    } finally {
      setLoadingWhisper(false);
    }
  };

  const handleAddVoice = async () => {
    if (!newVoice.id || !newVoice.name) {
      toast.error("Voice ID and Name are required");
      return;
    }
    
    try {
      const token = localStorage.getItem("token");
      await axios.post(
        `${API}/admin/openai-voices/${currentVoiceType}/add`,
        { voice: newVoice },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(`Added voice "${newVoice.name}" to ${currentVoiceType} voices`);
      setAddVoiceDialogOpen(false);
      setNewVoice({
        id: "",
        name: "",
        description: "",
        gender: "neutral",
        style: "",
        accent: "American",
        tier: "standard"
      });
      
      // Refresh the appropriate list
      if (currentVoiceType === "tts") {
        fetchTtsVoices();
      } else {
        fetchRealtimeVoices();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to add voice");
    }
  };

  const handleDeleteVoice = async (voiceType, voiceId, voiceName) => {
    if (!confirm(`Are you sure you want to delete "${voiceName}"?`)) {
      return;
    }
    
    try {
      const token = localStorage.getItem("token");
      await axios.delete(
        `${API}/admin/openai-voices/${voiceType}/${voiceId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(`Deleted voice "${voiceName}"`);
      
      if (voiceType === "tts") {
        fetchTtsVoices();
      } else {
        fetchRealtimeVoices();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete voice");
    }
  };

  const handleResetVoices = async (voiceType) => {
    if (!confirm(`Are you sure you want to reset ${voiceType} voices to defaults? This will remove any custom voices you've added.`)) {
      return;
    }
    
    try {
      const token = localStorage.getItem("token");
      await axios.post(
        `${API}/admin/openai-voices/${voiceType}/reset`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(`Reset ${voiceType} voices to defaults`);
      
      if (voiceType === "tts") {
        fetchTtsVoices();
      } else {
        fetchRealtimeVoices();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to reset voices");
    }
  };

  const handleAddWhisperModel = async () => {
    if (!newModel.id || !newModel.name) {
      toast.error("Model ID and Name are required");
      return;
    }
    
    try {
      const token = localStorage.getItem("token");
      await axios.post(
        `${API}/admin/whisper-models/add`,
        { model: newModel },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(`Added model "${newModel.name}"`);
      setAddModelDialogOpen(false);
      setNewModel({ id: "", name: "", description: "" });
      fetchWhisperModels();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to add model");
    }
  };

  const handleDeleteWhisperModel = async (modelId, modelName) => {
    if (!confirm(`Are you sure you want to delete "${modelName}"?`)) {
      return;
    }
    
    try {
      const token = localStorage.getItem("token");
      await axios.delete(
        `${API}/admin/whisper-models/${modelId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(`Deleted model "${modelName}"`);
      fetchWhisperModels();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete model");
    }
  };

  const handleResetWhisperModels = async () => {
    if (!confirm("Are you sure you want to reset Whisper models to defaults?")) {
      return;
    }
    
    try {
      const token = localStorage.getItem("token");
      await axios.post(
        `${API}/admin/whisper-models/reset`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success("Reset Whisper models to defaults");
      fetchWhisperModels();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to reset models");
    }
  };

  const handleSyncVoicesFromLatest = async (voiceType) => {
    setSyncingVoices(true);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(
        `${API}/admin/openai-voices/${voiceType}/sync-latest`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(`Synced ${response.data.voices?.length || 0} ${voiceType} voices from latest known list`);
      
      if (voiceType === "tts") {
        fetchTtsVoices();
      } else {
        fetchRealtimeVoices();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to sync ${voiceType} voices`);
    } finally {
      setSyncingVoices(false);
    }
  };

  const handleSyncWhisperFromOpenAI = async () => {
    setSyncingWhisper(true);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(
        `${API}/admin/whisper-models/sync-from-openai`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(`Synced ${response.data.models?.length || 0} models from OpenAI API`);
      fetchWhisperModels();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to sync from OpenAI. Make sure your OpenAI API key is configured.");
    } finally {
      setSyncingWhisper(false);
    }
  };

  const VoiceCard = ({ voice, voiceType, onDelete }) => (
    <div className="flex items-center justify-between p-4 bg-white border rounded-lg hover:shadow-sm transition-shadow">
      <div className="flex items-center gap-4">
        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-500 to-indigo-600 flex items-center justify-center">
          <Volume2 className="w-5 h-5 text-white" />
        </div>
        <div>
          <div className="flex items-center gap-2">
            <span className="font-medium text-slate-900">{voice.name}</span>
            <Badge variant="outline" className="text-xs">{voice.id}</Badge>
            {voice.tier && (
              <Badge 
                variant="secondary" 
                className={`text-xs ${voice.tier === 'standard' ? 'bg-green-100 text-green-700' : 'bg-purple-100 text-purple-700'}`}
              >
                {voice.tier}
              </Badge>
            )}
          </div>
          <p className="text-sm text-slate-500">{voice.description || "No description"}</p>
          <div className="flex gap-2 mt-1">
            {voice.gender && <Badge variant="outline" className="text-xs">{voice.gender}</Badge>}
            {voice.accent && <Badge variant="outline" className="text-xs">{voice.accent}</Badge>}
          </div>
        </div>
      </div>
      <Button
        variant="ghost"
        size="sm"
        onClick={() => onDelete(voiceType, voice.id, voice.name)}
        className="text-red-500 hover:text-red-700 hover:bg-red-50"
      >
        <Trash2 className="w-4 h-4" />
      </Button>
    </div>
  );

  const ModelCard = ({ model, onDelete }) => (
    <div className="flex items-center justify-between p-4 bg-white border rounded-lg hover:shadow-sm transition-shadow">
      <div className="flex items-center gap-4">
        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center">
          <Mic className="w-5 h-5 text-white" />
        </div>
        <div>
          <div className="flex items-center gap-2">
            <span className="font-medium text-slate-900">{model.name}</span>
            <Badge variant="outline" className="text-xs">{model.id}</Badge>
          </div>
          <p className="text-sm text-slate-500">{model.description || "No description"}</p>
        </div>
      </div>
      <Button
        variant="ghost"
        size="sm"
        onClick={() => onDelete(model.id, model.name)}
        className="text-red-500 hover:text-red-700 hover:bg-red-50"
      >
        <Trash2 className="w-4 h-4" />
      </Button>
    </div>
  );

  return (
    <div className="container mx-auto py-6 px-4 max-w-6xl">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
          <Settings2 className="w-7 h-7 text-purple-600" />
          Voice & Model Configuration
        </h1>
        <p className="text-slate-600 mt-1">
          Manage OpenAI voices and speech-to-text models available in your application
        </p>
      </div>

      <Alert className="mb-6 border-blue-200 bg-blue-50">
        <Info className="h-4 w-4 text-blue-600" />
        <AlertTitle className="text-blue-800">How it works</AlertTitle>
        <AlertDescription className="text-blue-700">
          <strong>Whisper Models:</strong> Click "Fetch from OpenAI" to automatically retrieve available transcription models from OpenAI's API.<br/>
          <strong>TTS/Realtime Voices:</strong> OpenAI doesn't provide an API to list voices. Click "Sync Latest" to update with the latest known voices, or manually add new ones.
        </AlertDescription>
      </Alert>

      <Tabs defaultValue="realtime" className="space-y-6">
        <TabsList className="grid w-full grid-cols-3 lg:w-[500px]">
          <TabsTrigger value="realtime" className="flex items-center gap-2">
            <Volume2 className="w-4 h-4" />
            Realtime Voices
          </TabsTrigger>
          <TabsTrigger value="tts" className="flex items-center gap-2">
            <Volume2 className="w-4 h-4" />
            TTS Voices
          </TabsTrigger>
          <TabsTrigger value="whisper" className="flex items-center gap-2">
            <Mic className="w-4 h-4" />
            Whisper Models
          </TabsTrigger>
        </TabsList>

        {/* Realtime Voices Tab */}
        <TabsContent value="realtime">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    OpenAI Realtime Voices
                    <Badge variant="secondary">{realtimeVoices.length} voices</Badge>
                  </CardTitle>
                  <CardDescription>
                    Voices available for live Voice AI calls. These are used in the Voice AI feature.
                  </CardDescription>
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleSyncVoicesFromLatest("realtime")}
                    disabled={syncingVoices || loadingRealtime}
                  >
                    <CloudDownload className={`w-4 h-4 mr-2 ${syncingVoices ? 'animate-pulse' : ''}`} />
                    Sync Latest
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleResetVoices("realtime")}
                    disabled={loadingRealtime}
                  >
                    <RefreshCw className={`w-4 h-4 mr-2 ${loadingRealtime ? 'animate-spin' : ''}`} />
                    Reset to Defaults
                  </Button>
                  <Dialog open={addVoiceDialogOpen && currentVoiceType === "realtime"} onOpenChange={(open) => {
                    setAddVoiceDialogOpen(open);
                    if (open) setCurrentVoiceType("realtime");
                  }}>
                    <DialogTrigger asChild>
                      <Button size="sm">
                        <Plus className="w-4 h-4 mr-2" />
                        Add Voice
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="sm:max-w-[500px]">
                      <DialogHeader>
                        <DialogTitle>Add New Realtime Voice</DialogTitle>
                        <DialogDescription>
                          Add a new OpenAI Realtime voice. Check OpenAI's documentation for the latest available voices.
                        </DialogDescription>
                      </DialogHeader>
                      <div className="grid gap-4 py-4">
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <Label htmlFor="voice-id">Voice ID *</Label>
                            <Input
                              id="voice-id"
                              value={newVoice.id}
                              onChange={(e) => setNewVoice({...newVoice, id: e.target.value})}
                              placeholder="e.g., river"
                            />
                          </div>
                          <div>
                            <Label htmlFor="voice-name">Display Name *</Label>
                            <Input
                              id="voice-name"
                              value={newVoice.name}
                              onChange={(e) => setNewVoice({...newVoice, name: e.target.value})}
                              placeholder="e.g., River"
                            />
                          </div>
                        </div>
                        <div>
                          <Label htmlFor="voice-desc">Description</Label>
                          <Textarea
                            id="voice-desc"
                            value={newVoice.description}
                            onChange={(e) => setNewVoice({...newVoice, description: e.target.value})}
                            placeholder="e.g., Calm and soothing voice"
                            rows={2}
                          />
                        </div>
                        <div className="grid grid-cols-3 gap-4">
                          <div>
                            <Label htmlFor="voice-gender">Gender</Label>
                            <Select value={newVoice.gender} onValueChange={(v) => setNewVoice({...newVoice, gender: v})}>
                              <SelectTrigger>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="neutral">Neutral</SelectItem>
                                <SelectItem value="male">Male</SelectItem>
                                <SelectItem value="female">Female</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          <div>
                            <Label htmlFor="voice-accent">Accent</Label>
                            <Input
                              id="voice-accent"
                              value={newVoice.accent}
                              onChange={(e) => setNewVoice({...newVoice, accent: e.target.value})}
                              placeholder="American"
                            />
                          </div>
                          <div>
                            <Label htmlFor="voice-tier">Tier</Label>
                            <Select value={newVoice.tier} onValueChange={(v) => setNewVoice({...newVoice, tier: v})}>
                              <SelectTrigger>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="standard">Standard</SelectItem>
                                <SelectItem value="realtime-exclusive">Realtime Exclusive</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                      </div>
                      <DialogFooter>
                        <Button variant="outline" onClick={() => setAddVoiceDialogOpen(false)}>
                          Cancel
                        </Button>
                        <Button onClick={handleAddVoice}>
                          Add Voice
                        </Button>
                      </DialogFooter>
                    </DialogContent>
                  </Dialog>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {loadingRealtime ? (
                <div className="flex items-center justify-center py-8">
                  <RefreshCw className="w-6 h-6 animate-spin text-slate-400" />
                </div>
              ) : (
                <div className="space-y-3">
                  {realtimeVoices.map((voice) => (
                    <VoiceCard
                      key={voice.id}
                      voice={voice}
                      voiceType="realtime"
                      onDelete={handleDeleteVoice}
                    />
                  ))}
                  {realtimeVoices.length === 0 && (
                    <p className="text-center text-slate-500 py-8">No realtime voices configured</p>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* TTS Voices Tab */}
        <TabsContent value="tts">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    OpenAI TTS Voices
                    <Badge variant="secondary">{ttsVoices.length} voices</Badge>
                  </CardTitle>
                  <CardDescription>
                    Voices available for Text-to-Speech. These are used for voice previews.
                  </CardDescription>
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleSyncVoicesFromLatest("tts")}
                    disabled={syncingVoices || loadingTts}
                  >
                    <CloudDownload className={`w-4 h-4 mr-2 ${syncingVoices ? 'animate-pulse' : ''}`} />
                    Sync Latest
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleResetVoices("tts")}
                    disabled={loadingTts}
                  >
                    <RefreshCw className={`w-4 h-4 mr-2 ${loadingTts ? 'animate-spin' : ''}`} />
                    Reset to Defaults
                  </Button>
                  <Dialog open={addVoiceDialogOpen && currentVoiceType === "tts"} onOpenChange={(open) => {
                    setAddVoiceDialogOpen(open);
                    if (open) setCurrentVoiceType("tts");
                  }}>
                    <DialogTrigger asChild>
                      <Button size="sm">
                        <Plus className="w-4 h-4 mr-2" />
                        Add Voice
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="sm:max-w-[500px]">
                      <DialogHeader>
                        <DialogTitle>Add New TTS Voice</DialogTitle>
                        <DialogDescription>
                          Add a new OpenAI TTS voice. Check OpenAI's documentation for the latest available voices.
                        </DialogDescription>
                      </DialogHeader>
                      <div className="grid gap-4 py-4">
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <Label htmlFor="tts-voice-id">Voice ID *</Label>
                            <Input
                              id="tts-voice-id"
                              value={newVoice.id}
                              onChange={(e) => setNewVoice({...newVoice, id: e.target.value})}
                              placeholder="e.g., river"
                            />
                          </div>
                          <div>
                            <Label htmlFor="tts-voice-name">Display Name *</Label>
                            <Input
                              id="tts-voice-name"
                              value={newVoice.name}
                              onChange={(e) => setNewVoice({...newVoice, name: e.target.value})}
                              placeholder="e.g., River"
                            />
                          </div>
                        </div>
                        <div>
                          <Label htmlFor="tts-voice-desc">Description</Label>
                          <Textarea
                            id="tts-voice-desc"
                            value={newVoice.description}
                            onChange={(e) => setNewVoice({...newVoice, description: e.target.value})}
                            placeholder="e.g., Calm and soothing voice"
                            rows={2}
                          />
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <Label htmlFor="tts-voice-gender">Gender</Label>
                            <Select value={newVoice.gender} onValueChange={(v) => setNewVoice({...newVoice, gender: v})}>
                              <SelectTrigger>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="neutral">Neutral</SelectItem>
                                <SelectItem value="male">Male</SelectItem>
                                <SelectItem value="female">Female</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          <div>
                            <Label htmlFor="tts-voice-accent">Accent</Label>
                            <Input
                              id="tts-voice-accent"
                              value={newVoice.accent}
                              onChange={(e) => setNewVoice({...newVoice, accent: e.target.value})}
                              placeholder="American"
                            />
                          </div>
                        </div>
                      </div>
                      <DialogFooter>
                        <Button variant="outline" onClick={() => setAddVoiceDialogOpen(false)}>
                          Cancel
                        </Button>
                        <Button onClick={handleAddVoice}>
                          Add Voice
                        </Button>
                      </DialogFooter>
                    </DialogContent>
                  </Dialog>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {loadingTts ? (
                <div className="flex items-center justify-center py-8">
                  <RefreshCw className="w-6 h-6 animate-spin text-slate-400" />
                </div>
              ) : (
                <div className="space-y-3">
                  {ttsVoices.map((voice) => (
                    <VoiceCard
                      key={voice.id}
                      voice={voice}
                      voiceType="tts"
                      onDelete={handleDeleteVoice}
                    />
                  ))}
                  {ttsVoices.length === 0 && (
                    <p className="text-center text-slate-500 py-8">No TTS voices configured</p>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Whisper Models Tab */}
        <TabsContent value="whisper">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    Whisper Speech-to-Text Models
                    <Badge variant="secondary">{whisperModels.length} models</Badge>
                  </CardTitle>
                  <CardDescription>
                    OpenAI Whisper models available for speech-to-text transcription.
                  </CardDescription>
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="default"
                    size="sm"
                    onClick={handleSyncWhisperFromOpenAI}
                    disabled={syncingWhisper || loadingWhisper}
                  >
                    <CloudDownload className={`w-4 h-4 mr-2 ${syncingWhisper ? 'animate-pulse' : ''}`} />
                    {syncingWhisper ? 'Fetching...' : 'Fetch from OpenAI'}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleResetWhisperModels}
                    disabled={loadingWhisper}
                  >
                    <RefreshCw className={`w-4 h-4 mr-2 ${loadingWhisper ? 'animate-spin' : ''}`} />
                    Reset to Defaults
                  </Button>
                  <Dialog open={addModelDialogOpen} onOpenChange={setAddModelDialogOpen}>
                    <DialogTrigger asChild>
                      <Button size="sm">
                        <Plus className="w-4 h-4 mr-2" />
                        Add Model
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="sm:max-w-[425px]">
                      <DialogHeader>
                        <DialogTitle>Add New Whisper Model</DialogTitle>
                        <DialogDescription>
                          Add a new OpenAI Whisper model for speech-to-text.
                        </DialogDescription>
                      </DialogHeader>
                      <div className="grid gap-4 py-4">
                        <div>
                          <Label htmlFor="model-id">Model ID *</Label>
                          <Input
                            id="model-id"
                            value={newModel.id}
                            onChange={(e) => setNewModel({...newModel, id: e.target.value})}
                            placeholder="e.g., whisper-2"
                          />
                        </div>
                        <div>
                          <Label htmlFor="model-name">Display Name *</Label>
                          <Input
                            id="model-name"
                            value={newModel.name}
                            onChange={(e) => setNewModel({...newModel, name: e.target.value})}
                            placeholder="e.g., Whisper v2"
                          />
                        </div>
                        <div>
                          <Label htmlFor="model-desc">Description</Label>
                          <Textarea
                            id="model-desc"
                            value={newModel.description}
                            onChange={(e) => setNewModel({...newModel, description: e.target.value})}
                            placeholder="e.g., Improved accuracy model"
                            rows={2}
                          />
                        </div>
                      </div>
                      <DialogFooter>
                        <Button variant="outline" onClick={() => setAddModelDialogOpen(false)}>
                          Cancel
                        </Button>
                        <Button onClick={handleAddWhisperModel}>
                          Add Model
                        </Button>
                      </DialogFooter>
                    </DialogContent>
                  </Dialog>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {loadingWhisper ? (
                <div className="flex items-center justify-center py-8">
                  <RefreshCw className="w-6 h-6 animate-spin text-slate-400" />
                </div>
              ) : (
                <div className="space-y-3">
                  {whisperModels.map((model) => (
                    <ModelCard key={model.id} model={model} onDelete={handleDeleteWhisperModel} />
                  ))}
                  {whisperModels.length === 0 && (
                    <p className="text-center text-slate-500 py-8">No Whisper models configured</p>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
